import { useMemo, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import type { Version } from '@keboola/api-client/developerPortal/types';
import { Button, EmptyState, Modal, Skeleton, Table, toast } from '@keboola/design';

import { QueryError } from '../../../components/QueryError';
import { getApiErrorMessage } from '../../auth/errors';
import { useRevertAppVersion } from '../../vendors/queries';
import type { AttributeChange } from '../versions';
import { diffVersions, newestFirst, revertPreview } from '../versions';

import { AttributeDiff } from './AttributeDiff';

const PAGE = 30;

const text = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const changedOn = (version: Version): string => {
  const at = text(version.updatedOn) ?? version.createdOn;
  return at === undefined ? '—' : new Date(at).toLocaleString();
};
const changedBy = (version: Version): string => text(version.updatedBy) ?? version.createdBy ?? '—';

type Shown =
  | { kind: 'diff'; version: Version; changes: AttributeChange[] }
  | { kind: 'revert'; version: Version; changes: AttributeChange[] };

/**
 * A component's versions, newest first, with a diff and a revert per version.
 *
 * - Each row diffs against the version before it.
 * - "Revert to this version" previews the change from the current version, then rolls back.
 * - `versions` is the vendor list for a vendor and the admin list, with `waveTags`, for an admin.
 */
export function VersionHistory({
  vendorId,
  appId,
  versions,
}: {
  vendorId: string;
  appId: string;
  versions: UseQueryResult<Version[]>;
}) {
  const revert = useRevertAppVersion(vendorId, appId);
  const [limit, setLimit] = useState(PAGE);
  const [shown, setShown] = useState<Shown | null>(null);

  const sorted = useMemo(() => newestFirst(versions.data ?? []), [versions.data]);
  const current = sorted[0];
  const rows = useMemo(
    () =>
      sorted.slice(0, limit).map((version, index) => {
        const previous = sorted[index + 1];
        return { version, changes: previous === undefined ? [] : diffVersions(previous, version) };
      }),
    [sorted, limit],
  );

  /** Diffs against a freshly fetched current version, so an edit made elsewhere is not hidden. */
  const openRevert = async (version: Version) => {
    const { data } = await versions.refetch();
    const latest = newestFirst(data ?? [])[0];
    if (latest === undefined) return;
    if (latest.version === version.version) {
      toast.info(`Version ${version.version} is already the current version.`);
      return;
    }
    setShown({ kind: 'revert', version, changes: revertPreview(latest, version) });
  };

  const confirmRevert = async (version: Version) => {
    if (version.version === undefined) return;
    try {
      await revert.mutateAsync(version.version);
      toast.success(`Reverted to version ${version.version}.`);
      setShown(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not revert the component.'));
    }
  };

  return (
    <>
      {versions.isLoading && <Skeleton className="tw:h-64" />}

      <QueryError
        isError={versions.isError}
        hasData={versions.data !== undefined}
        error={versions.error}
        onRetry={() => void versions.refetch()}
        loadTitle="Could not load the versions"
        refreshTitle="Could not refresh the versions"
        staleNote="Showing the versions from the last successful load."
        titleAs="h2"
      />

      {versions.data !== undefined && sorted.length === 0 && (
        <EmptyState variant="compact">
          <EmptyState.Description>This component has no stored versions.</EmptyState.Description>
        </EmptyState>
      )}

      {rows.length > 0 && (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>Version</Table.Head>
              <Table.Head>Changed on</Table.Head>
              <Table.Head>Changed by</Table.Head>
              <Table.Head>Attributes</Table.Head>
              <Table.Head>
                <span className="tw:sr-only">Actions</span>
              </Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map(({ version, changes }) => (
              <Table.Row key={version.version}>
                <Table.Cell>
                  {version.version}
                  {version === current && ' (current)'}
                </Table.Cell>
                <Table.Cell>{changedOn(version)}</Table.Cell>
                <Table.Cell>{changedBy(version)}</Table.Cell>
                <Table.Cell>
                  <span className="tw:text-sm">
                    {changes.map((change) => change.attribute).join(', ')}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <div className="tw:flex tw:flex-wrap tw:justify-end tw:gap-2">
                    {changes.length > 0 && (
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => setShown({ kind: 'diff', version, changes })}
                      >
                        Show diff
                      </Button>
                    )}
                    {current !== undefined && version !== current && (
                      <Button
                        variant="secondary"
                        size="small"
                        disabled={versions.isFetching}
                        onClick={() => void openRevert(version)}
                      >
                        Revert to this version
                      </Button>
                    )}
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {sorted.length > limit && (
        <div className="tw:flex tw:justify-center">
          <Button variant="secondary" onClick={() => setLimit((l) => l + PAGE)}>
            Show older versions
          </Button>
        </div>
      )}

      <Modal open={shown !== null} onOpenChange={(open) => !open && setShown(null)}>
        <Modal.Content size="large">
          <Modal.Header
            description={
              shown?.kind === 'revert'
                ? 'Reverting creates a new version with every setting of this version.'
                : shown === null
                  ? undefined
                  : `${changedOn(shown.version)} · ${changedBy(shown.version)}`
            }
          >
            {shown?.kind === 'revert'
              ? `Revert to version ${shown.version.version}?`
              : `Changes in version ${shown?.version.version ?? ''}`}
          </Modal.Header>
          <Modal.Body>
            <div className="tw:flex tw:flex-col tw:gap-6">
              {shown?.kind === 'revert' && shown.changes.length === 0 && (
                <p className="tw:m-0">This version has the same settings as the current one.</p>
              )}
              {shown?.changes.map((change) => (
                <AttributeDiff
                  key={change.attribute}
                  attribute={change.attribute}
                  oldValue={change.oldValue}
                  newValue={change.newValue}
                  leftTitle={shown.kind === 'revert' ? 'Current' : 'Before'}
                  rightTitle={shown.kind === 'revert' ? 'After revert' : 'After'}
                />
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Close asChild>
              <Button type="button" variant="secondary">
                {shown?.kind === 'revert' ? 'Cancel' : 'Close'}
              </Button>
            </Modal.Close>
            {shown?.kind === 'revert' && (
              <Button
                type="button"
                variant="primary"
                disabled={revert.isPending}
                onClick={() => void confirmRevert(shown.version)}
              >
                Revert
              </Button>
            )}
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </>
  );
}
